import { useQuery } from '@tanstack/react-query';
import { apiBatch, unwrapBatchOptional } from '@/api/batch';
import type {
    Collection,
    Tag,
    PaginatedResponse,
    LedgerType,
} from '@/api/types';
import { resolveApiPath } from '@/contexts/GoApiContext';

interface UseBatchEditFormDataParams {
    type: LedgerType;
    enabled: boolean;
}

export interface EditFormData {
    collections: Collection[];
    tags: Tag[];
}

/**
 * Hook to lazy-load collections and tags for the ledger edit form.
 * Only fetches when enabled is true (e.g., when Edit button is clicked).
 */
export function useBatchEditFormData({ type, enabled }: UseBatchEditFormDataParams) {
    return useQuery({
        queryKey: ['edit-form-data', type],
        queryFn: async (): Promise<EditFormData> => {
            const result = await apiBatch<{
                collections: PaginatedResponse<Collection>;
                tags: PaginatedResponse<Tag>;
            }>([
                { id: 'collections', path: resolveApiPath('/api/collections') + `?type=${type}` },
                { id: 'tags', path: resolveApiPath('/api/tags') + `?type=${type}` },
            ]);

            return {
                collections: unwrapBatchOptional(result.responses.collections, null)?.items ?? [],
                tags: unwrapBatchOptional(result.responses.tags, null)?.items ?? [],
            };
        },
        enabled,
        staleTime: 60000, // Cache for 1 minute
    });
}
